import { DEFAULT_MEDIA_FILTER_PRESET_ID, getMediaFilterPreset, MediaFilterBlendMode, MediaFilterPresetId } from './media-filter-presets';
import { getMediaFilterGrainTextureCanvas, getMediaFilterLutData, getMediaFilterOverlayTextureCanvas } from './media-filter-renderer';

const VERTEX_SHADER = `
attribute vec2 a_position;
attribute vec2 a_tex_coord;
varying vec2 v_tex_coord;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_tex_coord = a_tex_coord;
}
`;

const FRAGMENT_SHADER = `
precision mediump float;

varying vec2 v_tex_coord;
uniform sampler2D u_video_texture;
uniform sampler2D u_lut_texture;
uniform sampler2D u_overlay_texture;
uniform sampler2D u_grain_texture;
uniform float u_lut_size;
uniform float u_use_lut;
uniform float u_has_overlay;
uniform float u_has_grain;
uniform float u_overlay_opacity;
uniform float u_grain_opacity;
uniform float u_overlay_blend_mode;
uniform float u_grain_blend_mode;

float apply_soft_light(float base, float overlay) {
  if (overlay <= 0.5) {
    return base - ((1.0 - (2.0 * overlay)) * base * (1.0 - base));
  }

  float curve = base <= 0.25
    ? (((16.0 * base) - 12.0) * base + 4.0) * base
    : sqrt(base);

  return base + (((2.0 * overlay) - 1.0) * (curve - base));
}

vec3 blend_rgb(vec3 base, vec3 overlay, float mode) {
  if (mode < 0.5) {
    return overlay;
  }

  if (mode < 1.5) {
    return 1.0 - ((1.0 - base) * (1.0 - overlay));
  }

  if (mode < 2.5) {
    return base * overlay;
  }

  if (mode < 3.5) {
    vec3 low = 2.0 * base * overlay;
    vec3 high = 1.0 - (2.0 * (1.0 - base) * (1.0 - overlay));
    return mix(low, high, step(vec3(0.5), base));
  }

  return vec3(
    apply_soft_light(base.r, overlay.r),
    apply_soft_light(base.g, overlay.g),
    apply_soft_light(base.b, overlay.b)
  );
}

vec3 apply_strip_lut(vec3 color) {
  float lut_size = max(u_lut_size, 2.0);
  float max_index = lut_size - 1.0;
  float blue_index = color.b * max_index;
  float blue_low = floor(blue_index);
  float blue_high = min(max_index, blue_low + 1.0);
  float blue_mix = blue_index - blue_low;

  float texel_x = 1.0 / (lut_size * lut_size);
  float texel_y = 1.0 / lut_size;

  float red = color.r * max_index;
  float green = color.g * max_index;

  vec2 low_uv = vec2(
    ((blue_low * lut_size) + red + 0.5) * texel_x,
    (green + 0.5) * texel_y
  );
  vec2 high_uv = vec2(
    ((blue_high * lut_size) + red + 0.5) * texel_x,
    (green + 0.5) * texel_y
  );

  vec3 low_color = texture2D(u_lut_texture, low_uv).rgb;
  vec3 high_color = texture2D(u_lut_texture, high_uv).rgb;

  return mix(low_color, high_color, blue_mix);
}

void main() {
  vec4 sampled = texture2D(u_video_texture, vec2(v_tex_coord.x, 1.0 - v_tex_coord.y));
  vec3 color = sampled.rgb;

  if (u_use_lut > 0.5) {
    color = apply_strip_lut(color);
  }

  if (u_has_overlay > 0.5) {
    vec3 overlay_color = texture2D(u_overlay_texture, v_tex_coord).rgb;
    color = mix(color, blend_rgb(color, overlay_color, u_overlay_blend_mode), u_overlay_opacity);
  }

  if (u_has_grain > 0.5) {
    vec3 grain_color = texture2D(u_grain_texture, v_tex_coord).rgb;
    color = mix(color, blend_rgb(color, grain_color, u_grain_blend_mode), u_grain_opacity);
  }

  gl_FragColor = vec4(color, sampled.a);
}
`;

export class MediaFilterVideoWebglRenderer {
  private gl: WebGLRenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private positionBuffer: WebGLBuffer | null = null;
  private texCoordBuffer: WebGLBuffer | null = null;
  private videoTexture: WebGLTexture | null = null;
  private lutTexture: WebGLTexture | null = null;
  private overlayTexture: WebGLTexture | null = null;
  private grainTexture: WebGLTexture | null = null;
  private animationFrameId?: number;
  private videoFrameRequestId?: number;
  private running = false;
  private initialized = false;
  private currentPresetId: MediaFilterPresetId = DEFAULT_MEDIA_FILTER_PRESET_ID;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly video: HTMLVideoElement,
  ) {}

  public setPreset(presetId: MediaFilterPresetId): void {
    this.currentPresetId = presetId;
    if (!this.initialized) {
      return;
    }

    this.resize();
    this.updateRecipeTextures();
    this.drawFrame();
  }

  public start(): boolean {
    if (this.running) {
      this.resize();
      this.updateRecipeTextures();
      return true;
    }

    if (!this.initialized && !this.initialize()) {
      return false;
    }

    this.running = true;
    this.resize();
    this.updateRecipeTextures();
    this.scheduleNextFrame();

    return true;
  }

  public stop(): void {
    this.running = false;
    if (this.animationFrameId) {
      window.cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }

    const requestVideoFrameCallback = (this.video as HTMLVideoElement & {
      cancelVideoFrameCallback?: (handle: number) => void;
    }).cancelVideoFrameCallback;

    if (requestVideoFrameCallback && this.videoFrameRequestId) {
      requestVideoFrameCallback.call(this.video, this.videoFrameRequestId);
      this.videoFrameRequestId = undefined;
    }
  }

  public resize(): void {
    if (!this.initialized || !this.gl) {
      return;
    }

    const devicePixelRatio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(this.canvas.clientWidth * devicePixelRatio));
    const height = Math.max(1, Math.round(this.canvas.clientHeight * devicePixelRatio));
    if (this.canvas.width !== width) {
      this.canvas.width = width;
    }
    if (this.canvas.height !== height) {
      this.canvas.height = height;
    }

    this.gl.viewport(0, 0, width, height);
  }

  public drawFrame(): void {
    if (!this.initialized || !this.gl || !this.program || this.video.readyState < 2) {
      return;
    }

    this.updateVideoTexture();
    this.gl.clearColor(0, 0, 0, 1);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  public destroy(): void {
    this.stop();

    if (!this.gl) {
      return;
    }

    if (this.videoTexture) {
      this.gl.deleteTexture(this.videoTexture);
      this.videoTexture = null;
    }
    if (this.lutTexture) {
      this.gl.deleteTexture(this.lutTexture);
      this.lutTexture = null;
    }
    if (this.overlayTexture) {
      this.gl.deleteTexture(this.overlayTexture);
      this.overlayTexture = null;
    }
    if (this.grainTexture) {
      this.gl.deleteTexture(this.grainTexture);
      this.grainTexture = null;
    }
    if (this.positionBuffer) {
      this.gl.deleteBuffer(this.positionBuffer);
      this.positionBuffer = null;
    }
    if (this.texCoordBuffer) {
      this.gl.deleteBuffer(this.texCoordBuffer);
      this.texCoordBuffer = null;
    }
    if (this.program) {
      this.gl.deleteProgram(this.program);
      this.program = null;
    }

    this.initialized = false;
    this.gl = null;
  }

  private initialize(): boolean {
    this.gl = this.canvas.getContext('webgl', {
      alpha: false,
      antialias: true,
      depth: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      stencil: false,
    });

    if (!this.gl) {
      return false;
    }

    const vertexShader = this.compileShader(this.gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragmentShader = this.compileShader(this.gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertexShader || !fragmentShader) {
      return false;
    }

    const program = this.gl.createProgram();
    if (!program) {
      return false;
    }

    this.gl.attachShader(program, vertexShader);
    this.gl.attachShader(program, fragmentShader);
    this.gl.linkProgram(program);

    if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
      this.gl.deleteProgram(program);
      return false;
    }

    this.gl.deleteShader(vertexShader);
    this.gl.deleteShader(fragmentShader);
    this.program = program;
    this.gl.useProgram(program);
    this.positionBuffer = this.bindStaticBuffer('a_position', new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      1, 1,
    ]));
    this.texCoordBuffer = this.bindStaticBuffer('a_tex_coord', new Float32Array([
      0, 0,
      1, 0,
      0, 1,
      1, 1,
    ]));

    this.videoTexture = this.createTexture();
    this.lutTexture = this.createTexture();
    this.overlayTexture = this.createTexture();
    this.grainTexture = this.createTexture();

    if (!this.videoTexture || !this.lutTexture || !this.overlayTexture || !this.grainTexture) {
      return false;
    }

    this.bindTextureToUnit(this.videoTexture, 0, 'u_video_texture');
    this.bindTextureToUnit(this.lutTexture, 1, 'u_lut_texture');
    this.bindTextureToUnit(this.overlayTexture, 2, 'u_overlay_texture');
    this.bindTextureToUnit(this.grainTexture, 3, 'u_grain_texture');
    this.writePixelTexture(this.overlayTexture, [128, 128, 128, 255]);
    this.writePixelTexture(this.grainTexture, [128, 128, 128, 255]);

    this.initialized = true;
    return true;
  }

  private scheduleNextFrame(): void {
    if (!this.running) {
      return;
    }

    const requestVideoFrameCallback = (this.video as HTMLVideoElement & {
      requestVideoFrameCallback?: (callback: (now: number, metadata: unknown) => void) => number;
    }).requestVideoFrameCallback;

    if (requestVideoFrameCallback) {
      this.videoFrameRequestId = requestVideoFrameCallback.call(this.video, () => {
        this.videoFrameRequestId = undefined;
        this.drawFrame();
        this.scheduleNextFrame();
      });
      return;
    }

    this.animationFrameId = window.requestAnimationFrame(() => {
      this.animationFrameId = undefined;
      this.drawFrame();
      this.scheduleNextFrame();
    });
  }

  private updateRecipeTextures(): void {
    if (!this.gl || !this.program || !this.lutTexture || !this.overlayTexture || !this.grainTexture) {
      return;
    }

    const preset = getMediaFilterPreset(this.currentPresetId);
    const lutData = getMediaFilterLutData(this.currentPresetId);
    this.bindTextureToUnit(this.lutTexture, 1, 'u_lut_texture');
    this.writeCanvasTexture(this.lutTexture, lutData.textureCanvas);
    this.setUniform1f('u_lut_size', lutData.size);
    this.setUniform1f('u_use_lut', this.currentPresetId === DEFAULT_MEDIA_FILTER_PRESET_ID ? 0 : 1);

    const overlayCanvas = getMediaFilterOverlayTextureCanvas(this.currentPresetId, this.canvas.width, this.canvas.height);
    this.bindTextureToUnit(this.overlayTexture, 2, 'u_overlay_texture');
    if (overlayCanvas && preset.overlayBlendMode && (preset.overlayOpacity || 0) > 0) {
      this.writeCanvasTexture(this.overlayTexture, overlayCanvas);
      this.setUniform1f('u_has_overlay', 1);
      this.setUniform1f('u_overlay_opacity', preset.overlayOpacity || 0);
      this.setUniform1f('u_overlay_blend_mode', resolveBlendModeValue(preset.overlayBlendMode));
    } else {
      this.writePixelTexture(this.overlayTexture, [128, 128, 128, 255]);
      this.setUniform1f('u_has_overlay', 0);
      this.setUniform1f('u_overlay_opacity', 0);
      this.setUniform1f('u_overlay_blend_mode', resolveBlendModeValue('soft-light'));
    }

    const grainCanvas = getMediaFilterGrainTextureCanvas(this.currentPresetId, this.canvas.width, this.canvas.height);
    this.bindTextureToUnit(this.grainTexture, 3, 'u_grain_texture');
    if (grainCanvas && preset.grainBlendMode && (preset.grainOpacity || 0) > 0) {
      this.writeCanvasTexture(this.grainTexture, grainCanvas);
      this.setUniform1f('u_has_grain', 1);
      this.setUniform1f('u_grain_opacity', preset.grainOpacity || 0);
      this.setUniform1f('u_grain_blend_mode', resolveBlendModeValue(preset.grainBlendMode));
    } else {
      this.writePixelTexture(this.grainTexture, [128, 128, 128, 255]);
      this.setUniform1f('u_has_grain', 0);
      this.setUniform1f('u_grain_opacity', 0);
      this.setUniform1f('u_grain_blend_mode', resolveBlendModeValue('overlay'));
    }
  }

  private updateVideoTexture(): void {
    if (!this.gl || !this.videoTexture) {
      return;
    }

    this.bindTextureToUnit(this.videoTexture, 0, 'u_video_texture');
    this.gl.pixelStorei(this.gl.UNPACK_FLIP_Y_WEBGL, 0);
    this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, this.video);
  }

  private compileShader(type: number, source: string): WebGLShader | null {
    if (!this.gl) {
      return null;
    }

    const shader = this.gl.createShader(type);
    if (!shader) {
      return null;
    }

    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      this.gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  private bindStaticBuffer(attributeName: string, data: Float32Array): WebGLBuffer | null {
    if (!this.gl || !this.program) {
      return null;
    }

    const buffer = this.gl.createBuffer();
    if (!buffer) {
      return null;
    }

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, data, this.gl.STATIC_DRAW);
    const location = this.gl.getAttribLocation(this.program, attributeName);
    this.gl.enableVertexAttribArray(location);
    this.gl.vertexAttribPointer(location, 2, this.gl.FLOAT, false, 0, 0);

    return buffer;
  }

  private createTexture(): WebGLTexture | null {
    if (!this.gl) {
      return null;
    }

    const texture = this.gl.createTexture();
    if (!texture) {
      return null;
    }

    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

    return texture;
  }

  private bindTextureToUnit(texture: WebGLTexture, unit: number, uniformName: string): void {
    if (!this.gl || !this.program) {
      return;
    }

    this.gl.activeTexture(this.gl.TEXTURE0 + unit);
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.setUniform1i(uniformName, unit);
  }

  private writeCanvasTexture(texture: WebGLTexture, source: HTMLCanvasElement): void {
    if (!this.gl) {
      return;
    }

    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, source);
  }

  private writePixelTexture(texture: WebGLTexture, pixel: [number, number, number, number]): void {
    if (!this.gl) {
      return;
    }

    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.gl.texImage2D(
      this.gl.TEXTURE_2D,
      0,
      this.gl.RGBA,
      1,
      1,
      0,
      this.gl.RGBA,
      this.gl.UNSIGNED_BYTE,
      new Uint8Array(pixel),
    );
  }

  private setUniform1f(name: string, value: number): void {
    if (!this.gl || !this.program) {
      return;
    }

    const location = this.gl.getUniformLocation(this.program, name);
    if (location !== null) {
      this.gl.uniform1f(location, value);
    }
  }

  private setUniform1i(name: string, value: number): void {
    if (!this.gl || !this.program) {
      return;
    }

    const location = this.gl.getUniformLocation(this.program, name);
    if (location !== null) {
      this.gl.uniform1i(location, value);
    }
  }
}

function resolveBlendModeValue(mode: MediaFilterBlendMode): number {
  switch (mode) {
    case 'screen':
      return 1;
    case 'multiply':
      return 2;
    case 'overlay':
      return 3;
    case 'soft-light':
      return 4;
    case 'source-over':
    default:
      return 0;
  }
}
